import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import GoogleIcon from "@mui/icons-material/Google";
import { isGoogleSignInEnabled } from "../../config/environment";
import { CognitoError, signInWithPassword } from "../../auth/cognito";
import { completeSignIn } from "../../auth/session";
import { PendingProblemStart, startGoogleSignIn } from "../../auth/googleRedirect";
import { ApiError, confirmSignUp, resendCode, signUp } from "../../utils/api";
import { useAppDispatch } from "../../hooks";
import { signedIn } from "../../store/auth/authSlice";
import { trackEvent, GA_EVENTS } from "../../utils/analytics";
import { COLOR_PRIMARY, COLOR_TEXT_MUTED } from "../../styles/colors";

/** Why the dialog opened, which sets its heading. */
export type AuthReason = "practice" | "resume" | "menu";

type Step = "signUp" | "signIn" | "verify";

export const PASSWORD_HINT = "At least 8 characters, with upper and lower case letters and a number";
export const FREE_ACCOUNT_LINE = "Practice problems need a free account so we can keep track of your attempts. No credit card, no catch.";

interface AuthDialogProps {
  open: boolean;
  reason: AuthReason;
  /** The problem the player was working on (reason "resume"). */
  problemTitle?: string;
  /** Replayed after Google sign-in, which leaves the page. */
  pendingProblem?: PendingProblemStart;
  onClose: () => void;
  /** Called once the player is signed in; `isNewAccount` after a sign-up. */
  onSignedIn: (isNewAccount: boolean) => void;
}

function headingFor(reason: AuthReason, problemTitle?: string): { title: string; subtitle: string } {
  switch (reason) {
    case "practice":
      return { title: "Create a free account to practice", subtitle: FREE_ACCOUNT_LINE };
    case "resume":
      return { title: `Sign in to keep working on ${problemTitle ?? "this problem"}.`, subtitle: FREE_ACCOUNT_LINE };
    default:
      return { title: "Sign in or create a free account", subtitle: "Accounts are free. No credit card, no catch." };
  }
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof CognitoError) {
    if (err.code === "NotAuthorizedException") return "Incorrect email or password.";
    if (err.code === "TooManyRequestsException" || err.code === "LimitExceededException") {
      return "Too many attempts. Please try again later.";
    }
    return err.message;
  }
  return "Couldn't reach the server. Check your connection and try again.";
}

export const AuthDialog: React.FC<AuthDialogProps> = ({
  open,
  reason,
  problemTitle,
  pendingProblem,
  onClose,
  onSignedIn,
}) => {
  const dispatch = useAppDispatch();
  const [step, setStep] = useState<Step>("signUp");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [emailTaken, setEmailTaken] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const showGoogle = isGoogleSignInEnabled();

  // Every opening starts on a clean "Create free account" form
  useEffect(() => {
    if (open) {
      setStep("signUp");
      setPassword("");
      setCode("");
      setError(null);
      setEmailTaken(false);
      setInfo(null);
      setBusy(false);
    }
  }, [open]);

  const { title, subtitle } = headingFor(reason, problemTitle);

  const switchStep = (next: Step) => {
    setStep(next);
    setError(null);
    setEmailTaken(false);
    setInfo(null);
  };

  const finishSignIn = async (isNewAccount: boolean) => {
    const tokens = await signInWithPassword(email, password);
    const profile = await completeSignIn(tokens);
    dispatch(signedIn({ email: profile?.email ?? email.trim().toLowerCase(), name: profile?.name }));
    trackEvent(isNewAccount ? GA_EVENTS.SIGN_UP_COMPLETED : GA_EVENTS.SIGN_IN, { method: "email" });
    onSignedIn(isNewAccount);
  };

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setEmailTaken(false);
    try {
      await action();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSignUp = () =>
    run(async () => {
      trackEvent(GA_EVENTS.SIGN_UP_STARTED, { method: "email" });
      try {
        await signUp(email.trim(), password, name.trim() || undefined);
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) setEmailTaken(true);
        throw err;
      }
      setCode("");
      setInfo(null);
      setStep("verify");
    });

  const handleVerify = () =>
    run(async () => {
      await confirmSignUp(email.trim(), code.trim());
      await finishSignIn(true);
    });

  const handleSignIn = () =>
    run(async () => {
      try {
        await finishSignIn(false);
      } catch (err) {
        if (err instanceof CognitoError && err.code === "UserNotConfirmedException") {
          // Never verified: send a fresh code and pick up where they left off
          await resendCode(email.trim());
          setCode("");
          setStep("verify");
          setInfo("Your email isn't verified yet. We sent you a new code.");
          return;
        }
        throw err;
      }
    });

  const handleResend = () =>
    run(async () => {
      await resendCode(email.trim());
      setInfo("We sent you a new code.");
    });

  const handleGoogle = () => {
    trackEvent(GA_EVENTS.SIGN_UP_STARTED, { method: "google" });
    setBusy(true);
    startGoogleSignIn(window.location.pathname, pendingProblem).catch(() => {
      setBusy(false);
      setError("Couldn't start Google sign-in. Please try again.");
    });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (step === "signUp") handleSignUp();
    else if (step === "signIn") handleSignIn();
    else handleVerify();
  };

  const googleButton = showGoogle && (
    <>
      <Button
        fullWidth
        variant="outlined"
        startIcon={<GoogleIcon />}
        onClick={handleGoogle}
        disabled={busy}
        data-testid="google-sign-in-btn"
        sx={{ textTransform: "none", fontWeight: 600 }}
      >
        Continue with Google
      </Button>
      <Divider sx={{ color: COLOR_TEXT_MUTED, fontSize: 13 }}>or</Divider>
    </>
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} aria-labelledby="auth-dialog-title" maxWidth="xs" fullWidth>
      <DialogTitle id="auth-dialog-title" sx={{ fontWeight: 700, color: COLOR_PRIMARY, pr: 6 }}>
        {title}
        <Typography component="span" variant="body2" sx={{ display: "block", color: COLOR_TEXT_MUTED, mt: 0.5 }}>
          {subtitle}
        </Typography>
        <IconButton aria-label="close" onClick={onClose} disabled={busy} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {step !== "verify" && (
          <Tabs
            value={step}
            onChange={(_e, value: Step) => switchStep(value)}
            variant="fullWidth"
            sx={{ mb: 2 }}
          >
            <Tab value="signUp" label="Create free account" sx={{ textTransform: "none" }} data-testid="sign-up-tab" />
            <Tab value="signIn" label="Sign in" sx={{ textTransform: "none" }} data-testid="sign-in-tab" />
          </Tabs>
        )}

        <Box component="form" onSubmit={submit} noValidate sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {step !== "verify" && googleButton}

          {step === "verify" ? (
            <>
              <Typography variant="body2" data-testid="verify-message">
                We emailed a 6-digit code to <strong>{email.trim()}</strong>.
              </Typography>
              <TextField
                label="Verification code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                inputProps={{ inputMode: "numeric", autoComplete: "one-time-code", maxLength: 10 }}
                autoFocus
                fullWidth
              />
            </>
          ) : (
            <>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                fullWidth
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={step === "signUp" ? "new-password" : "current-password"}
                helperText={step === "signUp" ? PASSWORD_HINT : undefined}
                fullWidth
              />
              {step === "signUp" && (
                <TextField
                  label="Name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  fullWidth
                />
              )}
            </>
          )}

          {info && <Alert severity="info">{info}</Alert>}
          {error && (
            <Alert severity="error" data-testid="auth-error">
              {error}
              {emailTaken && (
                <>
                  {" "}
                  <Link component="button" type="button" onClick={() => switchStep("signIn")} data-testid="sign-in-instead">
                    Sign in instead
                  </Link>
                </>
              )}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={busy}
            data-testid="auth-submit-btn"
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {step === "signUp" ? "Create free account" : step === "signIn" ? "Sign in" : "Verify"}
          </Button>

          {step === "verify" && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Link component="button" type="button" variant="body2" onClick={handleResend} disabled={busy}>
                Resend code
              </Link>
              <Link component="button" type="button" variant="body2" onClick={() => switchStep("signUp")} disabled={busy}>
                Use a different email
              </Link>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
